import { store } from 'Models/Store';
import React, { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { userAccountKeys } from 'Modules/UserAccount/Hooks/userAccountKeys';
import { UserInvoicesCollection } from './UserInvoiceListCollection';
import { fetchUserInvoices, UserInvoicesCountDto } from 'Services/Api/_HumanWritten/InvoiceService';

export interface UserInvoiceListProps {
	userId?: string;
	bookingType?: string;
}

export function UserInvoiceListComponent({ userId, bookingType }: UserInvoiceListProps) {
	const perPage = 10;
	const [pageNum, setPageNum] = useState<number>(0);
	const [searchInput, setSearchInput] = useState<string>('');
	const [search, setSearch] = useState<string>('');

	// Debounce search input
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (searchInput !== search) {
				setSearch(searchInput);
				setPageNum(0);
			}
		}, 500);
		return () => clearTimeout(timeoutId);
	}, [searchInput]);

	// keepPreviousData keeps the current page visible while the next one loads, so pagination
	// and search don't flash an empty list; staleTime 0 so every change refetches.
	const { data: invoices, dataUpdatedAt } = useQuery({
		queryKey: userAccountKeys.invoicePage(userId, { pageNum, search }),
		queryFn: async () => {
			const x = await fetchUserInvoices(userId as string, pageNum * perPage, perPage, search || undefined);
			return x.data as UserInvoicesCountDto;
		},
		enabled: isNotNullOrUndefined(userId) || isNotNullOrUndefined(store?.userId),
		placeholderData: keepPreviousData,
		staleTime: 0,
	});

	const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt) : null;

	return (
		<div className="user-invoice-list__container">
			<UserInvoicesCollection
				invoices={invoices?.invoices ?? null}
				totalInvoiceCount={invoices?.count ?? 0}
				filtered={invoices?.filtered ?? false}
				perPage={perPage}
				pageNum={pageNum}
				setPageNum={setPageNum}
				searchInput={searchInput}
				setSearchInput={setSearchInput}
				lastUpdated={lastUpdated}
			/>
		</div>
	);
}
